import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { stopPolling } from "../../utils/polling";
import logout from "../../utils/logout";
import { formatNumber } from "../../utils/data";
import { getAuth } from "firebase/auth";
import { getAPIClient, DocketAPIError } from "../../apiClient";
import { Modal } from "../../components/modals/Modal";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { useAtom } from "jotai";
import {
  userAtom,
  highContrastAtom,
  biometricAuthAtom,
  consentAcceptedAtomFamily,
} from "../../jotai/atoms";
import { DocketSwitch } from "../../components/form/DocketSwitch";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Dialog } from "@capacitor/dialog";
import { BiometricAuth, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { getBiometricLabel } from "../../utils/biometrics";
import { vibrateDevice } from "../../utils/capacitor";

type SettingsItem = {
  title: string;
  callback: () => void;
  id?: string;
  toggleValue?: boolean;
  nativeOnly?: boolean;
  hide?: boolean;
};

export default function MobileMenu() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useAtom(userAtom);
  const [showDeleteWebModal, setShowDeleteWebModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string | boolean>(false);
  const [showUpdateWebEmailModal, setShowUpdateWebEmailModal] = useState(false);
  const [highContrast, setHighContrast] = useAtom(highContrastAtom);
  const [biometricAuth, setBiometricAuth] = useAtom(biometricAuthAtom);
  const [biometryType, setBiometryType] = useState<BiometryType | null>(null);
  const [version, setVersion] = useState<string>("");
  const [consentAccepted, setConsentAccepted] = useAtom(
    consentAcceptedAtomFamily(user?.email ?? "")
  );

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      BiometricAuth.checkBiometry().then((value) => {
        if (value.isAvailable) {
          setBiometryType(value.biometryType);
        }
      });

      loadVersion();
    }
  }, []);

  const deleteUserAccount = async () => {
    try {
      setDeletingAccount(true);
      const client = getAPIClient();
      await client.deleteUser();
      setConsentAccepted(false);
      await signOut();
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  const showDeleteModal = async () => {
    if (!Capacitor.isNativePlatform()) {
      setShowDeleteWebModal(true);
    } else {
      const { value } = await Dialog.confirm({
        title: t("settings.delete_account"),
        message: t("settings.delete_account_message"),
        okButtonTitle: t("generic.delete"),
        cancelButtonTitle: t("generic.cancel"),
      });
      if (value) {
        deleteUserAccount();
      }
    }
  };

  const showUpdateEmailModal = async () => {
    if (!Capacitor.isNativePlatform()) {
      setShowUpdateWebEmailModal(true);
    } else {
      await Dialog.alert({
        title: "Update Account Email",
        message:
          "Docket® does not support changing your account email address. Please delete your account and create a new account with your preferred email.",
        buttonTitle: t("generic.ok"),
      });
    }
  };

  const signOut = async () => {
    stopPolling();
    await logout(getAPIClient(), getAuth());
    navigate("/");
  };

  const toggleBiometricAuth = async () => {
    if (biometryType && !biometricAuth) {
      try {
        await BiometricAuth.authenticate();
        await vibrateDevice();
        setBiometricAuth(true);
      } catch (error) {
        setBiometricAuth(false);
      }
    } else {
      setBiometricAuth(false);
    }
  };

  const toggleHighContrast = async () => {
    if (!highContrast) await vibrateDevice();
    setHighContrast(!highContrast);
  };

  let accountItems: SettingsItem[] = [
    {
      title: "Update Profile",
      callback: () =>
        navigate("/home/settings/profile", { state: { back: true, title: "Account Info" } }),
      id: "update-profile",
    },
    {
      title: "Notification Preferences",
      callback: () =>
        navigate("/home/settings/notifications", {
          state: { back: true, title: "Notification Preferences" },
        }),
      id: "notification-preferences",
    },
    {
      title: user?.verified_phone_number
        ? `Phone ${formatNumber(user.verified_phone_number)}`
        : "Phone",
      callback: () =>
        navigate("/home/settings/phone", { state: { back: true, title: "Verify Phone" } }),
      id: "phone-settings",
    },
    {
      title: biometryType ? getBiometricLabel(biometryType) : "",
      callback: () => toggleBiometricAuth(),
      toggleValue: biometricAuth,
      nativeOnly: true,
      hide: biometryType === null || biometryType === BiometryType.none,
      id: "biometric-auth",
    },
    {
      title: "Update Account Email",
      callback: () => showUpdateEmailModal(),
      id: "update-email",
    },
    {
      title: "High-contrast Mode",
      callback: () => toggleHighContrast(),
      id: "high-contrast-mode",
      toggleValue: highContrast,
    },
    {
      title: "Delete Account",
      callback: () => showDeleteModal(),
      id: "delete-account",
    },
  ];

  if (!Capacitor.isNativePlatform()) {
    accountItems = accountItems.filter((i) => !i.nativeOnly);
  }

  const aboutItems: SettingsItem[] = [
    {
      title: "Immunization Records",
      callback: () =>
        navigate("/home/records-policy", { state: { back: true, title: "Immunization Records" } }),
      id: "immunization-records",
    },
    {
      title: "Privacy Policy",
      callback: () => navigate("/home/privacy", { state: { back: true, title: "Privacy Policy" } }),
      id: "privacy-policy",
    },
    {
      title: "Terms of Use",
      callback: () => navigate("/home/terms", { state: { back: true, title: "Terms of Use" } }),
      id: "terms-of-use",
    },
    {
      title: "COVID-19 Privacy Policy",
      callback: () =>
        navigate("/home/covid-policy", { state: { back: true, title: "COVID-19 Privacy Policy" } }),
      id: "covid-policy",
    },
    {
      title: "About Docket",
      callback: () => navigate("/home/about", { state: { back: true, title: "About Docket®" } }),
      id: "about",
    },
    {
      title: "Share Feedback",
      callback: () =>
        navigate("/home/feedback", { state: { back: true, title: "Share Feedback" } }),
      id: "feedback",
    },
  ];

  const loadVersion = async () => {
    if (!Capacitor.isNativePlatform()) return;

    const version = await App.getInfo();
    setVersion(`- version ${version.version}.p${version.build}`);
  };

  return (
    <div className="w-full flex flex-col font-semibold text-xl mt-[10px]">
      {showErrorMessage && (
        <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>{showErrorMessage}</ErrorModal>
      )}
      <Modal
        id="email-modal"
        open={showUpdateWebEmailModal}
        title="Update Account Email"
        onCloseModal={() => setShowUpdateWebEmailModal(false)}
        hasClose={true}
        actionButtons={
          <>
            <button
              className="btn docket-button"
              aria-label={t("generic.ok")}
              onClick={() => setShowUpdateWebEmailModal(false)}
            >
              {t("generic.ok")}
            </button>
          </>
        }
      >
        <p>
          Docket® does not support changing your account email address. Please delete your account
          and create a new account with your preferred email.
        </p>
      </Modal>
      <Modal
        id="delete-modal"
        open={showDeleteWebModal}
        title={t("settings.delete_account")}
        onCloseModal={() => setShowDeleteWebModal(false)}
        hasClose={true}
        actionButtons={
          <>
            <button
              className="btn docket-button secondary"
              aria-label={t("generic.cancel")}
              onClick={() => setShowDeleteWebModal(false)}
            >
              {t("generic.cancel")}
            </button>
            <button
              disabled={deletingAccount}
              className="btn docket-button"
              aria-label={t("generic.delete")}
              onClick={() => deleteUserAccount()}
              data-testid="delete-confirm"
            >
              {t("generic.delete")}
            </button>
          </>
        }
      >
        <p>{t("settings.delete_account_message")}</p>
      </Modal>
      <div className="bg-pager-blue px-4 py-6">
        <p className="text-white">Account</p>
      </div>
      <div className="bg-white flex flex-col px-6">
        {accountItems
          .filter((i) => !i.hide)
          .map((item, i) => (
            <div
              key={i}
              className={`flex cursor-pointer py-6 ${
                i < accountItems.length - 1 ? "border-b-[1px] border-border-gray" : ""
              }`}
              onClick={item.toggleValue === undefined ? item.callback : undefined}
              data-testid={item.id}
            >
              <p className="text-black mr-auto">{item.title}</p>
              {item.toggleValue !== undefined ? (
                <div className="pt-[5px]">
                  <DocketSwitch
                    checked={item.toggleValue}
                    onChange={item.callback}
                    id={item.title}
                    name={item.title}
                    data-testid={`${item.id}-toggle`}
                  />
                </div>
              ) : (
                <img
                  src="/assets/images/chevron_right_grey.svg"
                  className="h-[16px] w-[16px] my-auto"
                />
              )}
            </div>
          ))}
      </div>
      <div className="bg-pager-blue px-4 py-6">
        <p data-testid="about-version" className="text-white">
          About {version}
        </p>
      </div>
      <div className="bg-white flex flex-col px-6">
        {aboutItems.map((item, i) => (
          <div
            key={i}
            className={`flex cursor-pointer py-6 ${
              i < aboutItems.length - 1 ? "border-b-[1px] border-border-gray" : ""
            }`}
            onClick={item.callback}
            data-testid={item.id}
          >
            <p className="text-black">{item.title}</p>
            <img
              src="/assets/images/chevron_right_grey.svg"
              className="h-[16px] w-[16px] ml-auto my-auto"
            />
          </div>
        ))}
      </div>
      <div className="bg-pager-blue px-4 py-6">
        <p className="text-white">Sign Out</p>
      </div>
      <div className="bg-white flex flex-col px-6">
        <div className="flex cursor-pointer py-6" onClick={signOut} data-testid="sign-out">
          <p className="text-black">Sign Out</p>
          <img
            src="/assets/images/chevron_right_grey.svg"
            className="h-[16px] w-[16px] ml-auto my-auto"
          />
        </div>
      </div>
    </div>
  );
}
